import React from 'react'

import { clsx } from 'clsx'
import { useAtomValue } from 'jotai'

import { mapRoute } from '@dfds-pax/utils'

import { Skeleton } from '@/app/[locale]/[ferry]/Skeleton'
import { SelectedRoutes } from '@/states/selection.interface'
import { selectedDateAtom, selectedDeparturesAtom, selectedRoutesAtom } from '@/states/selectionAtom'

import { getCompoundRoute, isCompoundRouteUsingRoute } from '@/utils/routeHelpers'

import { CompoundRouteHeader } from '../CompoundRouteHeader'
import { JourneySwitch } from '../JourneySwitch'
import { JourneyType } from '../JourneyType'
import { RouteHeader } from '../RouteHeader'
import { FiltersSkeleton } from './FiltersSkeleton'
import RowSkeleton from './RowSkeleton'

interface Props {
  returnLeg?: boolean
}

// mapRoute throws on anything it doesn't know, and the route headers map both legs unconditionally.
const isMappable = (route: string | null): boolean => {
  if (!route || route === 'undefined' || route === 'ONE_WAY') return false
  try {
    mapRoute(route)
    return true
  } catch {
    return false
  }
}

const canRenderRouteHeader = ({ homebound, outbound }: SelectedRoutes): boolean =>
  isMappable(outbound) && (!homebound || isMappable(homebound))

const RouteHeaderSkeleton = (): React.ReactElement => (
  <div className="gap-semantic-global-2xs flex flex-col">
    <Skeleton className="h-8 w-25" />
    <Skeleton className="h-5 w-75 lg:h-6" />
  </div>
)

// The route/date header is already known from the URL and session, so it renders for real and only
// the filters and departure rows are skeletons. Structure mirrors DepartureLeg.
export const LegSkeleton = ({ returnLeg }: Props): React.ReactElement => {
  const selectedRoutes = useAtomValue(selectedRoutesAtom)
  const selectedDepartures = useAtomValue(selectedDeparturesAtom)
  const selectedDate = useAtomValue(selectedDateAtom)
  const hasCompoundRoute = Boolean(getCompoundRoute(returnLeg ? selectedRoutes.homebound : selectedRoutes.outbound))

  // A leg with a departure already selected reloads collapsed: only that one row, no filters. The
  // return leg stays empty until an outbound is picked.
  const legSelected = Boolean(returnLeg ? selectedDepartures.departureHome?.id : selectedDepartures.departureOut?.id)
  const awaitingOutbound = Boolean(returnLeg && !selectedDepartures.departureOut?.id)
  const rowCount = legSelected ? 1 : awaitingOutbound ? 0 : 8

  const editElement = legSelected ? <Skeleton className="h-6 w-30" /> : undefined

  return (
    <div className="bg-semantic-global-background-light-grey flex flex-col">
      <div className="mb-semantic-global-md md:mb-semantic-global-lg">
        {!canRenderRouteHeader(selectedRoutes) ? (
          <RouteHeaderSkeleton />
        ) : isCompoundRouteUsingRoute(selectedRoutes.outbound) ? (
          <CompoundRouteHeader
            editElement={editElement}
            isOpen={true}
            returnLeg={returnLeg}
            selectedRouteFilters={{ frcqf: true, frdkk: true }}
          />
        ) : (
          <RouteHeader editElement={editElement} isOpen={true} returnLeg={returnLeg} />
        )}
        {!legSelected && !awaitingOutbound ? <FiltersSkeleton showRouteFilter={hasCompoundRoute} /> : null}
      </div>
      <div className="gap-semantic-global-md md:gap-semantic-global-lg flex flex-col">
        {Array.from({ length: rowCount }).map((_, i) => (
          <RowSkeleton key={i} />
        ))}
      </div>
      {!returnLeg ? (
        // The journey type is known from the URL, so it renders for real but stays inert until loaded.
        <div className={clsx('pointer-events-none', rowCount > 0 && 'mt-semantic-global-md md:mt-semantic-global-lg')}>
          <JourneyType
            switchElement={
              <JourneySwitch
                attr="onewayReturnSwitch"
                isReturnJourney={Boolean(selectedDate?.returnDepartureDate)}
                name="returnOneWay"
                onChange={() => undefined}
              />
            }
          />
        </div>
      ) : null}
    </div>
  )
}
